// ============================================================
// THE LANE (Squad Mode stage 8, ruled 2026-10-23: "the lane adapts") - the
// strip at the plate's right where a kill-team brother's instrument stands:
// his life signs in Solo Mode, the B-scope in Squad Mode.
//
// It takes the free room first: a plate held by its height (the cell wider
// than the figure needs) has a margin at the right that costs the figure
// nothing. Where there is none (a plate held by its width), it may take up
// to ~8% of the figure, and narrows below that. Under a floor width it is
// not drawn at all (the strip, the marks and the meter still say Squad
// Mode). Measured on the real app (2026-10-23): plates of 346 x 145-250 at
// 1280x720, 346 x 309-430 at 1440x900, ~346 x 511-638 at 1920x1080.
//
// Everything is in the frame's units; `px` is the lane's width on screen.
//
// Pure. Zero React dependency.
// ============================================================
import type { Frame } from './calloutLayout';

/** The lane's widths on screen, and the most of the figure's width it may cost where there is no free room. */
export const LANE = { min: 20, max: 70, budget: 0.08 } as const;

export interface Lane {
  /** The frame the plate draws: the figure's, widened at the right by the lane. */
  frame: Frame;
  /** The lane itself, at the frame's right, the frame's full height. */
  lane: Frame;
  /** Its width on screen. */
  px: number;
}

/** The lane's width in frame units, and on screen, for a frame held by its width (no free room). */
function costly(frame: Frame, cell: { w: number }): { e: number; px: number } {
  // As wide as LANE.max on screen, or as the budget allows, whichever is less.
  const forMax = cell.w > LANE.max ? (LANE.max * frame.w) / (cell.w - LANE.max) : Infinity;
  const forBudget = (LANE.budget / (1 - LANE.budget)) * frame.w;
  const e = Math.min(forMax, forBudget);
  return { e, px: (e * cell.w) / (frame.w + e) };
}

/** The lane for a frame drawn in a cell; null without a cell, or where it would be narrower than the floor. */
export function laneFor(frame: Frame, cell: { w: number; h: number } | null): Lane | null {
  if (!cell || cell.w <= 0 || cell.h <= 0) return null;
  const s = Math.min(cell.w / frame.w, cell.h / frame.h);
  const room = cell.w - frame.w * s;
  const { e, px } =
    room >= LANE.min ? { e: Math.min(LANE.max, room) / s, px: Math.min(LANE.max, room) } : costly(frame, cell);
  if (px < LANE.min) return null;
  return {
    frame: { ...frame, w: frame.w + e },
    lane: { x: frame.x + frame.w, y: frame.y, w: e, h: frame.h },
    px,
  };
}

/** What a plate's lane holds: a brother's life signs in Solo Mode, the B-scope in Squad Mode; nothing for anyone else. */
export type LaneKind = 'solo' | 'squad';

interface LaneBody {
  teamTag?: string;
  bodyType?: string;
  horde?: { isHorde?: boolean } | null;
  killTeam?: { on?: true };
}

/**
 * The lane for this body: only a person-shaped member of a kill-team WITH AN OATH (D8: hordes, vehicles and crowds never
 * have one); Squad Mode's instrument while he is in it, his life signs otherwise.
 */
export function laneKindOf(body: LaneBody, oathTags: ReadonlySet<string>): LaneKind | null {
  if (!body.teamTag || !oathTags.has(body.teamTag)) return null;
  if (body.horde?.isHorde || (body.bodyType && body.bodyType !== 'humanoid')) return null;
  return body.killTeam?.on ? 'squad' : 'solo';
}
